import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import If from 'Views/Components/If/If';
import { EntityFormMode } from '../../../Helpers/Common';
import { AccountDetailsTabHeader } from '../AccountDetailsTabHeader';
import { ReadOnlyField } from './ReadOnlyField';
import * as React from 'react';
import { UserEntity } from 'Models/Entities';
import { password, showCompanyNameField } from './AccountFieldsUtils';
import { upperCaseFirst } from 'Util/StringUtils';
import { appConfigStore } from 'Models/AppConfigStore';
import { useIntl } from 'react-intl';
import { RequestUserTypeButton } from 'Modules/UserTypeRequests/Components/RequestUserTypeButton';

interface DetailFieldsReadOnlyProps {
	user: UserEntity;
	mode: EntityFormMode;
	setMode: (mode: EntityFormMode) => void;
	updatePasswordButton?: React.ReactNode;
	staffView?: boolean;
	canEdit?: boolean;
	onConvertGuest?: () => void;
	/** Optional customer-type field rendered among the fields (staff user detail page only). */
	userTypeSlot?: React.ReactNode;
}

export function DetailFieldsReadOnly({
	user,
	mode,
	setMode,
	updatePasswordButton,
	staffView,
	canEdit = true,
	onConvertGuest,
	userTypeSlot,
}: DetailFieldsReadOnlyProps) {
	const intl = useIntl();
	const { userPostcodeEnabled, userPhoneEnabled } = appConfigStore;

	const isBusinessAccount = showCompanyNameField(user.userDisplayName, user.companyName);

	return (
		<div className={`user-account-fields${staffView ? ' user-account-fields--split' : ''}`}>
			<AccountDetailsTabHeader
				headerText="Account details"
				buttonText={mode === EntityFormMode.VIEW && canEdit ? 'Edit' : ''}
				onClick={() => setMode(EntityFormMode.EDIT)}
				secondaryButtonText={
					mode === EntityFormMode.VIEW && canEdit && isNotNullOrUndefined(onConvertGuest)
						? intl.formatMessage({
								id: 'user-account__convert-to-full-account',
								defaultMessage: 'Convert to full account',
							})
						: ''
				}
				onSecondaryClick={onConvertGuest}
			/>
			<If condition={isBusinessAccount}>
				<ReadOnlyField
					fieldName="Company name"
					// Company names are shown exactly as entered — no case conversion. Acronyms and
					// proper casing (e.g. "AMT", "Amity Trader") must be preserved.
					fieldValue={stringNotEmpty(user.companyName) ? user.companyName!.trim() : '-'}
				/>
			</If>
			<ReadOnlyField
				fieldName={isBusinessAccount ? 'Contact name' : 'Name'}
				// Names are non-nullable in the model but can still be null in the database, so guard
				// the case conversion and trim: ReadOnlyField renders an empty value as '-'.
				fieldValue={`${upperCaseFirst(user.firstName?.toLowerCase())} ${upperCaseFirst(
					user.lastName?.toLowerCase(),
				)}`.trim()}
			/>
			<ReadOnlyField
				fieldName="Password"
				fieldValue={password.password}
				updatePasswordButton={canEdit ? updatePasswordButton : undefined}
			/>
			<ReadOnlyField fieldName="Email" fieldValue={user.email} />
			{userPhoneEnabled && <ReadOnlyField fieldName="Mobile" fieldValue={user.phone} />}
			{userPostcodeEnabled && (
				<ReadOnlyField
					fieldName={intl.formatMessage({
						id: 'user-account__postcode',
						defaultMessage: 'Postcode',
					})}
					fieldValue={user.postcode}
				/>
			)}
			{/* Shown on the Communications tab, where it is also set. See DetailFields. */}
			{!staffView && !user.isGuest && (
				// Customers request an account type for themselves here; the staff-side
				// request/pending/reject flow lives in the user detail page's UserTypeSection.
				// Guests have no account type, so the request affordance is hidden for them.
				<div className="mt-4">
					<RequestUserTypeButton currentUserDisplayNameId={user.userDisplayNameId} />
				</div>
			)}
			{/* Created / last login moved to the user detail page header for staff. */}
			{userTypeSlot}
		</div>
	);
}
